import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Instagram } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to validate logo URLs
const isValidLogoUrl = (url) => {
  if (!url) return false;
  return url.includes('cloudfront.net') || 
         url.includes('amazonaws.com') || 
         url.includes('crystalmedia') ||
         url.startsWith('/');
};

const Header = ({ logoUrl, variant = 'light', blogVisible, inspoVisible }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const [isBlogVisible, setIsBlogVisible] = useState(blogVisible !== undefined ? blogVisible : true);
  const [isInspoVisible, setIsInspoVisible] = useState(inspoVisible !== undefined ? inspoVisible : true);
  const location = useLocation();

  useEffect(() => {
    if (blogVisible !== undefined) {
      setIsBlogVisible(blogVisible);
    }
    if (inspoVisible !== undefined) {
      setIsInspoVisible(inspoVisible);
    }
    if (blogVisible !== undefined && inspoVisible !== undefined) {
      return;
    }
    const fetchSettings = async () => {
      try {
        const res = await fetch(`${API_URL}/api/content/settings`);
        const data = await res.json();
        if (blogVisible === undefined) setIsBlogVisible(data.blog_visible !== false);
        if (inspoVisible === undefined) setIsInspoVisible(data.inspo_visible !== false);
      } catch (e) { /* default visible */ }
    };
    fetchSettings();
  }, [blogVisible, inspoVisible]);
  
  const navLinks = [
    { path: '/crystal-difference', label: 'Why Us' },
    ...(isBlogVisible ? [{ path: '/blog', label: 'Blog' }] : []),
    ...(isInspoVisible ? [{ path: '/inspo', label: 'Inspo' }] : []),
    { path: '/about', label: 'About' },
  ];

  // Always show both Photo and Film buttons on every page
  const prominentLinks = [
    { path: '/photo', label: 'Photo' },
    { path: '/film', label: 'Film' },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const validLogo = isValidLogoUrl(logoUrl) ? logoUrl : '';
  // "dark" and "transparent" both sit on dark backgrounds — light text needed
  const isDarkBg = variant === 'dark' || variant === 'transparent';
  // Gold accents only on the proper dark (film theatre) variant — landing's transparent uses white
  const useGold = variant === 'dark';

  return (
    <>
      <style>{`
        .menu-overlay {
          clip-path: circle(0% at calc(100% - 40px) 40px);
          transition: clip-path 0.7s cubic-bezier(0.77, 0, 0.175, 1);
        }
        .menu-overlay.open {
          clip-path: circle(150% at calc(100% - 40px) 40px);
        }
        .menu-item {
          opacity: 0;
          transform: translateY(20px) scale(0.96);
          filter: blur(4px);
          transition: opacity 0.5s cubic-bezier(0.23, 1, 0.32, 1), 
                      transform 0.5s cubic-bezier(0.23, 1, 0.32, 1),
                      filter 0.5s cubic-bezier(0.23, 1, 0.32, 1);
        }
        .menu-overlay.open .menu-item {
          opacity: 1;
          transform: translateY(0) scale(1);
          filter: blur(0);
        }
        .menu-overlay.open .menu-item:nth-child(1) { transition-delay: 0.2s; }
        .menu-overlay.open .menu-item:nth-child(2) { transition-delay: 0.26s; }
        .menu-overlay.open .menu-item:nth-child(3) { transition-delay: 0.32s; }
        .menu-overlay.open .menu-item:nth-child(4) { transition-delay: 0.38s; }
        .menu-overlay.open .menu-item:nth-child(5) { transition-delay: 0.44s; }
        .menu-overlay.open .menu-item:nth-child(6) { transition-delay: 0.50s; }
        .menu-overlay.open .menu-item:nth-child(7) { transition-delay: 0.56s; }
        .menu-overlay.open .menu-item:nth-child(8) { transition-delay: 0.62s; }
        .menu-link {
          position: relative;
        }
        .menu-link::after {
          content: '';
          position: absolute;
          bottom: -4px;
          left: 0;
          width: 0;
          height: 1px;
          background: currentColor;
          transition: width 0.3s ease;
        }
        .menu-link:hover::after {
          width: 100%;
        }
        .menu-footer {
          opacity: 0;
          transform: translateY(10px);
          transition: opacity 0.5s ease 0.6s, transform 0.5s ease 0.6s;
        }
        .menu-overlay.open .menu-footer {
          opacity: 1;
          transform: translateY(0);
        }
      `}</style>

      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 ${
        variant === 'transparent' ? 'bg-transparent' 
        : variant === 'dark' ? 'bg-black/90 backdrop-blur-md border-b border-white/5'
        : 'bg-white/90 backdrop-blur-md border-b border-black/5'
      }`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex-shrink-0 relative z-[60]">
              {validLogo && !logoError ? (
                <img 
                  src={validLogo} 
                  alt="Crystal Media" 
                  className="h-8 w-auto"
                  onError={() => setLogoError(true)}
                />
              ) : (
                <span className={`font-presti text-lg tracking-[0.2em] ${useGold ? 'text-[#D4A574]' : isDarkBg ? 'text-white' : 'text-black'}`}>
                  CRYSTAL
                </span>
              )}
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-6">
              {/* Prominent section buttons */}
              {prominentLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-4 py-1.5 text-xs tracking-[0.15em] uppercase border transition-all ${
                    useGold
                      ? 'border-[#D4A574]/40 text-[#D4A574] hover:bg-[#D4A574]/10'
                      : isDarkBg
                        ? 'border-white/40 text-white hover:bg-white hover:text-black'
                        : 'border-black/20 text-black/70 hover:bg-black hover:text-white'
                  }`}
                >
                  {link.label}
                </Link>
              ))}

              {/* Secondary links */}
              {navLinks.slice(1).map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-xs tracking-[0.15em] uppercase transition-colors ${
                    isActive(link.path)
                      ? (isDarkBg ? 'text-white' : 'text-black')
                      : (isDarkBg ? 'text-white/60 hover:text-white' : 'text-black/50 hover:text-black')
                  }`}
                >
                  {link.label}
                </Link>
              ))}

              {/* Inquire CTA */}
              <Link
                to="/inquire"
                className={`px-6 py-2.5 text-xs tracking-[0.15em] uppercase transition-colors ${
                  useGold
                    ? 'bg-[#D4A574] text-black hover:bg-[#D4A574]/85'
                    : isDarkBg
                      ? 'bg-white text-black hover:bg-white/90'
                      : 'bg-black text-white hover:bg-black/80'
                }`}
              >
                Inquire
              </Link>
            </nav>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`md:hidden relative z-[60] w-10 h-10 flex items-center justify-center transition-colors ${
                isMobileMenuOpen ? 'text-black' : (isDarkBg ? 'text-white' : 'text-black/70')
              }`}
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </header>

      {/* Fullscreen Mobile Menu Overlay */}
      <div 
        className={`menu-overlay fixed inset-0 z-[55] bg-[#fafafa] md:hidden ${isMobileMenuOpen ? 'open' : ''}`}
        style={{ pointerEvents: isMobileMenuOpen ? 'auto' : 'none' }}
      >
        {/* Close button inside overlay */}
        <button
          onClick={() => setIsMobileMenuOpen(false)}
          className="absolute top-6 right-6 z-[60] w-10 h-10 flex items-center justify-center text-black/70 hover:text-black transition-colors"
          aria-label="Close menu"
          data-testid="mobile-menu-close"
        >
          <X size={28} />
        </button>
        <div className="flex flex-col justify-center h-full px-12">
          <nav className="space-y-5">
            {/* Prominent section links first */}
            {prominentLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsMobileMenuOpen(false)}
                className="menu-item menu-link block font-presti text-4xl text-black transition-colors"
              >
                {link.label}
              </Link>
            ))}

            {/* Regular nav links */}
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`menu-item menu-link block font-presti text-3xl transition-colors ${
                  isActive(link.path) ? 'text-black' : 'text-black/40 hover:text-black'
                }`}
              >
                {link.label}
              </Link>
            ))}

            {/* Inquire Button - Always prominent */}
            <Link
              to="/inquire"
              onClick={() => setIsMobileMenuOpen(false)}
              className="menu-item menu-link block font-presti text-3xl text-black hover:text-black/70 transition-colors pt-4 border-t border-black/10"
            >
              Inquire
            </Link>
          </nav>
          
          {/* Social/Contact in mobile menu */}
          <div className="menu-footer absolute bottom-12 left-12 right-12">
            <Link
              to="/partners"
              data-testid="mobile-menu-partners"
              className="block text-[10px] tracking-[0.2em] uppercase text-black/45 hover:text-black transition-colors mb-3"
            >
              Partners Referral Program
            </Link>
            <a
              href="https://www.instagram.com/crystalmedia.ca/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-menu-instagram"
              className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-black/60 hover:text-black transition-colors"
            >
              <Instagram size={14} />
              @crystalmedia.ca
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
